import { AdvancedOptions, type FilterValues } from "../search/AdvancedOptions";
import { Input } from "../ui/input";
import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ClassroomCard } from "./ClassroomCard";
import { LessonCard } from "./LessonCard";
import { ReportDetail } from "./ReportDetail";
import { ReportList } from "./ReportList";
import { LessonReportList } from "./LessonReportList";
import { SearchBar } from "./SearchBar";
import { LessonSearchBar } from "./LessonSearchBar";
import {
  type AnyReportRow,
  type ClassroomReportRow,
  type LessonReportRow,
  type ReportTab,
} from "./types";

type SummaryScope = { organization_id?: string | null; organization_name?: string | null; teacher_id?: number | null; teacher_name?: string | null };
type ClassroomSummary = SummaryScope & { id: number; name: string; teacher_name: string | null };
type LessonSummary = SummaryScope & { id: number; name: string | null };

const REPORT_TABS: ReportTab[] = ["classroom", "lesson", "freestyle"];

export function ClassroomReports() {
  const [searchByTab, setSearchByTab] = useState<Record<ReportTab, { filters: FilterValues; nameSearch: string }>>({
    classroom: { filters: {}, nameSearch: "" },
    lesson: { filters: {}, nameSearch: "" },
    freestyle: { filters: {}, nameSearch: "" },
  });
  const [searchParams, setSearchParams] = useSearchParams();

  const tabParam = searchParams.get("reportTab") as ReportTab | null;
  const tab: ReportTab = tabParam && REPORT_TABS.includes(tabParam) ? tabParam : "classroom";
  const { filters, nameSearch } = searchByTab[tab];
  const setFilters = (filters: FilterValues) => setSearchByTab((current) => ({ ...current, [tab]: { ...current[tab], filters } }));
  const setNameSearch = (nameSearch: string) => setSearchByTab((current) => ({ ...current, [tab]: { ...current[tab], nameSearch } }));

  const classroomIdParam = searchParams.get("classroomId");
  const lessonIdParam = searchParams.get("lessonId");
  const reportIdParam = searchParams.get("reportId");

  const selectedClassroomId = classroomIdParam ? Number(classroomIdParam) : null;
  const selectedLessonId = lessonIdParam ? Number(lessonIdParam) : null;

  const [classrooms, setClassrooms] = useState<ClassroomSummary[]>([]);
  const [lessons, setLessons] = useState<LessonSummary[]>([]);

  useEffect(() => {
    (async () => {
      try {
        const [cr, lr] = await Promise.all([
          fetch("/api/internal/admin/classrooms-with-reports"),
          fetch("/api/internal/admin/lessons-with-reports"),
        ]);
        if (cr.ok) setClassrooms(await cr.json() as ClassroomSummary[]);
        if (lr.ok) setLessons(await lr.json() as LessonSummary[]);
      } catch { /* ignore */ }
    })();
  }, []);

  const summaries = tab === "classroom" ? classrooms : lessons;
  const matches = (item: ClassroomSummary | LessonSummary) =>
    (!nameSearch || `${item.name || ""} ${item.id}`.toLowerCase().includes(nameSearch.toLowerCase())) &&
    (!filters.organization_id || item.organization_id === filters.organization_id) &&
    (!filters.teacher_id || String(item.teacher_id) === filters.teacher_id);
  const summaryFilters = <div className="flex flex-wrap gap-2">
    <Input className="max-w-sm" placeholder="Search by name or ID" value={nameSearch} onChange={(event) => setNameSearch(event.target.value)} />
    <AdvancedOptions values={filters} onChange={setFilters} fields={[
      { key: "organization_id", label: "Organization", options: [...new Map(summaries.filter((item) => item.organization_id).map((item) => [item.organization_id!, { value: item.organization_id!, label: item.organization_name || item.organization_id! }])).values()] },
      { key: "teacher_id", label: "Teacher owner", options: [...new Map(summaries.filter((item) => item.teacher_id).map((item) => [item.teacher_id!, { value: String(item.teacher_id), label: `${item.teacher_name || "Teacher"} (#${item.teacher_id})` }])).values()] },
    ]} />
  </div>;
  const setTab = useCallback((value: ReportTab) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("reportTab", value);
      next.delete("classroomId");
      next.delete("lessonId");
      next.delete("reportId");
      return next;
    });
  }, [setSearchParams]);

  const selectClassroom = useCallback((id: number) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("classroomId", String(id));
      next.delete("reportId");
      return next;
    });
  }, [setSearchParams]);

  const selectLesson = useCallback((id: number) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("lessonId", String(id));
      next.delete("reportId");
      return next;
    });
  }, [setSearchParams]);

  const selectReport = useCallback((r: AnyReportRow | ClassroomReportRow | LessonReportRow) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("reportId", String(r.id));
      return next;
    });
  }, [setSearchParams]);

  const clearReport = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("reportId");
      return next;
    });
  }, [setSearchParams]);

  const clearClassroom = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("classroomId");
      next.delete("reportId");
      return next;
    });
  }, [setSearchParams]);

  const clearLesson = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("lessonId");
      next.delete("reportId");
      return next;
    });
  }, [setSearchParams]);

  const tabs = (
    <div className="flex gap-2 border-b">
      {(
        [
          { value: "classroom", label: "Daily Classroom" },
          { value: "lesson", label: "Lesson" },
          { value: "freestyle", label: "Free-style" },
        ] as { value: ReportTab; label: string }[]
      ).map(({ value, label }) => (
        <button
          key={value}
          type="button"
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            tab === value
              ? "border-primary text-foreground"
              : "border-transparent text-muted-foreground hover:text-foreground"
          }`}
          onClick={() => setTab(value)}
        >
          {label}
        </button>
      ))}
    </div>
  );

  return (
    <div className="flex h-full min-h-0 flex-col gap-4 w-full">
      {tabs}

      {tab === "classroom" &&
        (reportIdParam ? (
          <ReportDetail onBack={clearReport} />
        ) : selectedClassroomId !== null ? (
          <ReportList
            classroomId={selectedClassroomId}
            onBack={clearClassroom}
            onSelectReport={selectReport}
          />
        ) : (
          <div className="flex flex-col gap-4 w-full">
            <SearchBar onResult={selectReport} onClassroom={selectClassroom} />
            {summaryFilters}
            {classrooms.filter(matches).map((c) => (
              <ClassroomCard
                key={c.id}
                id={c.id}
                name={c.name}
                teacher_name={c.teacher_name}
                onClick={() => selectClassroom(c.id)}
              />
            ))}
          </div>
        ))}

      {tab === "lesson" &&
        (reportIdParam ? (
          <ReportDetail onBack={clearReport} />
        ) : selectedLessonId !== null ? (
          <LessonReportList
            lessonId={selectedLessonId}
            onBack={clearLesson}
            onSelectReport={selectReport}
          />
        ) : (
          <div className="flex flex-col gap-4 w-full">
            <LessonSearchBar onResult={selectReport} onLesson={selectLesson} />
            {summaryFilters}
            {lessons.filter(matches).map((l) => (
              <LessonCard
                key={l.id}
                id={l.id}
                name={l.name}
                onClick={() => selectLesson(l.id)}
              />
            ))}
          </div>
        ))}

      {tab === "freestyle" && (
        <p className="text-sm text-muted-foreground">Coming soon.</p>
      )}
    </div>
  );
}
